import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AvatarIcon, CountryFlag } from "@/lib/identity";
import {
  Play, Pause, ArrowFatRight, ClockClockwise, Trophy, X, ShieldCheck,
  Megaphone, Broadcast, ArrowCounterClockwise, Warning, FlagPennant, Crown,
  Check, Flag, EnvelopeSimple,
} from "@phosphor-icons/react";
import { Heading, Btn } from "./NKDaily";

const TABS = [
  { key: "pre", label: "Pre-Event", Icon: FlagPennant },
  { key: "invites", label: "Invitations", Icon: Megaphone },
  { key: "questions", label: "Questions", Icon: Check },
  { key: "live", label: "Live Console", Icon: Broadcast },
  { key: "post", label: "Post-Event", Icon: Trophy },
];

export default function ChampionshipOps() {
  const [tab, setTab] = useState("pre");
  const [champ, setChamp] = useState(null);

  const loadChamp = async () => {
    try {
      const { data } = await api.get("/championship/state");
      setChamp(data.championship);
    } catch { /* silent */ }
  };
  useEffect(() => {
    loadChamp();
    const id = setInterval(loadChamp, 5000);
    return () => clearInterval(id);
  }, []);

  const state = champ?.state || "IDLE";

  return (
    <div className="space-y-6" data-testid="championship-ops-page">
      <Heading
        title="Championship Ops"
        subtitle="Pre-event qualifier tools · live console · post-event resolution."
      />

      <StatusStrip champ={champ} state={state} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-nk-line overflow-x-auto">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`ops-tab-${key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 flex items-center gap-2 whitespace-nowrap transition-colors ${
              tab === key
                ? "border-nk-gold text-nk-gold"
                : "border-transparent text-nk-mute hover:text-nk-text"
            }`}
          >
            <Icon size={14} weight={tab === key ? "fill" : "bold"} />
            {label}
          </button>
        ))}
      </div>

      {tab === "pre" && <PreEvent champ={champ} onChanged={loadChamp} />}
      {tab === "invites" && <InvitesTab champ={champ} />}
      {tab === "questions" && <QuestionsTab champ={champ} />}
      {tab === "live" && <LiveConsole champ={champ} onChanged={loadChamp} />}
      {tab === "post" && <PostEvent champ={champ} onChanged={loadChamp} />}
    </div>
  );
}

function StatusStrip({ champ, state }) {
  const paused = !!champ?.paused;
  const badgeCls = state === "RUNNING"
    ? (paused ? "bg-nk-bad/15 text-nk-bad border-nk-bad/40" : "bg-nk-accent/15 text-nk-accent border-nk-accent/40")
    : state === "ENDED"
    ? "bg-nk-gold/15 text-nk-gold border-nk-gold/40"
    : "bg-nk-line/30 text-nk-mute border-nk-line";
  const label = state === "RUNNING" && paused ? "PAUSED" : state;
  return (
    <div className="nk-card p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border ${badgeCls}`}>
          {label}
        </span>
        {champ?.current_round != null && (
          <span className="text-xs font-mono text-nk-mute">
            Round <b className="text-nk-text">{champ.current_round}</b> / {champ.total_rounds}
          </span>
        )}
        {champ?.current_q_index >= 0 && (
          <span className="text-xs font-mono text-nk-mute">
            Q <b className="text-nk-text">{champ.current_q_index + 1}</b> / {champ.questions_per_round}
          </span>
        )}
        {champ?.country_cup?.winner && (
          <span className="text-[10px] font-mono uppercase text-nk-gold flex items-center gap-1">
            <Crown size={11} weight="fill" /> Country Cup · {champ.country_cup.winner}
          </span>
        )}
      </div>
      <p className="text-[11px] text-nk-dim font-mono">
        {champ?.season_number ? `Season ${champ.season_number}` : "No championship scheduled"}
      </p>
    </div>
  );
}

/* -------------------- PRE-EVENT TAB -------------------- */
function PreEvent({ champ, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dryRun, setDryRun] = useState(null);
  const [seed, setSeed] = useState("");
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/championship/admin/qualifiers-preview");
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load qualifiers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const resolveTie = async (country, winner, loser) => {
    if (!window.confirm(`Grant +1 KR to ${winner.username} to break tie against ${loser.username}?`)) return;
    try {
      await api.post("/championship/admin/resolve-tie", {
        country, winner_user_id: winner.user_id, loser_user_id: loser.user_id,
      });
      toast.success(`Tie resolved · ${winner.username} advances`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runDryRun = async () => {
    setRunning(true);
    try {
      const seedNum = seed ? parseInt(seed, 10) : null;
      const { data } = await api.post("/championship/admin/dry-run",
        seedNum != null && !Number.isNaN(seedNum) ? { seed: seedNum } : {});
      setDryRun(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dry run failed");
    } finally {
      setRunning(false);
    }
  };

  const startNow = async () => {
    const canStart = !champ || champ.state === "ENDED";
    if (!canStart) return toast.error("Reset current championship first");
    if (!window.confirm("Launch the International Championship NOW? Top-5 from each country will be locked in as seeds.")) return;
    try {
      await api.post("/championship/admin/start-now");
      toast.success("Championship started");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      {/* Qualifier standings */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <div>
            <p className="eyebrow">Live qualifier standings</p>
            <p className="text-xs text-nk-dim mt-1">Top {preview?.contenders_per_country ?? 5} per country lock in on Day 30.</p>
          </div>
          <Btn onClick={load} Icon={ArrowCounterClockwise} label="Refresh" tone="ghost" testid="ops-refresh-qualifiers" />
        </div>
        {loading ? (
          <p className="text-sm text-nk-dim py-8 text-center">Loading qualifiers…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {preview && Object.entries(preview.countries).map(([country, block]) => (
              <QualifierColumn key={country} country={country} block={block} onResolveTie={resolveTie} />
            ))}
          </div>
        )}
      </section>

      {/* Dry-run simulator */}
      <section className="nk-card p-5">
        <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
          <div>
            <p className="eyebrow">Dry-run bracket simulator</p>
            <p className="text-xs text-nk-dim mt-1">
              Simulate all {4} rounds with random RNG. NO writes — pure preview.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Seed (optional)"
              value={seed}
              onChange={(e) => setSeed(e.target.value.replace(/\D/g, "").slice(0, 8))}
              data-testid="ops-dryrun-seed"
              className="w-32 px-3 py-2 text-sm bg-nk-charcoal border border-nk-line rounded-sm focus:border-nk-gold outline-none font-mono"
            />
            <Btn onClick={runDryRun} Icon={Play} label={running ? "Simulating…" : "Run simulation"}
                 tone="accent" testid="ops-run-dryrun" />
          </div>
        </div>
        {dryRun?.ok === false && (
          <p className="text-sm text-nk-bad">{dryRun.error || "Simulation failed"}</p>
        )}
        {dryRun?.ok && <DryRunResult data={dryRun} />}
      </section>

      {/* Schedule Day 31 */}
      <ScheduleSection champ={champ} onChanged={onChanged} />

      {/* Season scoring pause (Day 31 orchestration) */}
      <ScoringPauseCard />

      {/* Launch */}
      <section className="nk-card p-5 border-nk-gold/40 bg-nk-gold/[0.03]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-display text-lg">Ready to launch?</p>
            <p className="text-xs text-nk-mute mt-1 max-w-md">
              Wipes any existing championship, snapshots current qualifiers, and starts the bracket immediately.
            </p>
          </div>
          <Btn onClick={startNow} Icon={Play} label="Launch tournament NOW" tone="gold"
               testid="ops-launch-tournament" />
        </div>
      </section>
    </div>
  );
}


function ScoringPauseCard() {
  const [season, setSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/season/current");
      setSeason(data);
    } catch (e) {
      // silent — season may not exist
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const paused = !!season?.paused_scoring;
  const day = season?.day;
  const forcedByDay = day >= 31; // auto-pause on Day 31+

  const toggle = async () => {
    setBusy(true);
    try {
      const url = paused ? "/season/admin/resume-scoring" : "/season/admin/pause-scoring";
      await api.post(url, {});
      toast.success(paused ? "Season scoring resumed" : "Season scoring paused");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="nk-card p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <p className="eyebrow flex items-center gap-2">
            <Pause size={12} /> Season SS scoring
          </p>
          <p className="text-xs text-nk-mute mt-1 max-w-md">
            While paused, regular NK Daily / Ladder / Duel / Survival gameplay
            <b> does not credit season_score</b>. KR still updates. Championship
            scoring is unaffected. Auto-pauses on Day 31.
          </p>
          {loading ? (
            <p className="text-xs text-nk-dim mt-2 font-mono">Loading…</p>
          ) : (
            <p className="text-xs mt-2 font-mono">
              <span className="text-nk-dim">State: </span>
              <span className={paused ? "text-nk-bad" : "text-nk-accent"}>
                {paused ? "PAUSED" : "LIVE"}
              </span>
              {day != null && (
                <span className="text-nk-dim"> · Day {day}</span>
              )}
              {forcedByDay && !paused && (
                <span className="text-nk-gold"> · auto-pause active (Day ≥ 31)</span>
              )}
            </p>
          )}
        </div>
        <Btn
          onClick={toggle}
          disabled={busy || loading}
          Icon={paused ? Play : Pause}
          label={busy ? "…" : (paused ? "Resume scoring" : "Pause scoring")}
          tone={paused ? "accent" : "gold"}
          testid="ops-season-pause-toggle"
        />
      </div>
    </section>
  );
}


function ScheduleSection({ champ, onChanged }) {
  const [saving, setSaving] = useState(false);
  const scheduled = champ?.scheduled_at;
  // Convert existing scheduled_at to a datetime-local input value (UTC)
  const toLocalInput = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  };
  const [local, setLocal] = useState(toLocalInput(scheduled));
  useEffect(() => { setLocal(toLocalInput(scheduled)); }, [scheduled]);

  const save = async () => {
    if (!local) return toast.error("Pick a date/time first");
    setSaving(true);
    try {
      const iso = new Date(local).toISOString();
      await api.post("/championship/admin/schedule", { scheduled_at: iso });
      toast.success("Day 31 scheduled");
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <section className="nk-card p-5">
      <p className="eyebrow mb-3">Schedule Day 31</p>
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] font-mono uppercase text-nk-dim block mb-1">Local date &amp; time</label>
          <input
            type="datetime-local"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            data-testid="ops-schedule-input"
            className="w-full px-3 py-2 text-sm bg-nk-charcoal border border-nk-line rounded-sm focus:border-nk-gold outline-none font-mono"
          />
          {scheduled && (
            <p className="text-[10px] font-mono text-nk-dim mt-1">
              Currently set: <span className="text-nk-gold">{new Date(scheduled).toLocaleString()}</span>
            </p>
          )}
        </div>
        <Btn onClick={save} Icon={Check} label={saving ? "Saving…" : "Save schedule"}
             tone="gold" testid="ops-schedule-save" />
      </div>
    </section>
  );
}


function QualifierColumn({ country, block, onResolveTie }) {
  const rows = block.rows || [];
  const ties = block.ties || [];
  return (
    <div className="nk-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <CountryFlag country={country} size={14} />
        <p className="font-display text-lg">{country === "NG" ? "Nigeria" : "Ghana"}</p>
        <span className="text-xs text-nk-dim ml-auto font-mono">
          {rows.filter(r => r.qualifying).length} qualifying
        </span>
      </div>

      <div className="space-y-1.5 mb-3">
        {rows.map((r, i) => (
          <div key={r.user_id}
               className={`flex items-center gap-3 px-2 py-2 rounded-sm border ${
                 r.qualifying ? "border-nk-gold/30" : "border-nk-line opacity-60"
               }`}>
            <span className={`font-mono text-xs w-5 text-center ${r.qualifying ? "text-nk-gold" : "text-nk-dim"}`}>
              #{r.rank}
            </span>
            <AvatarIcon id={r.avatar} size={22} />
            <span className="text-sm flex-1 truncate">
              {r.username}
              {r.unranked && <span className="ml-1.5 text-[9px] text-nk-bad font-mono uppercase">UNRANKED</span>}
            </span>
            <span className="font-mono text-xs text-nk-accent">
              {(r.season_score || 0).toLocaleString()}
            </span>
            <span className="font-mono text-[10px] text-nk-dim ml-2">
              KR {r.knowledge_rating}
            </span>
          </div>
        ))}
        {rows.length === 0 && <p className="text-xs text-nk-dim py-4 text-center">No qualifiers yet.</p>}
      </div>

      {ties.length > 0 && (
        <div className="mt-3 pt-3 border-t border-nk-line space-y-2">
          <p className="text-[10px] font-mono uppercase text-nk-bad flex items-center gap-1">
            <Warning size={11} weight="fill" /> Ties detected
          </p>
          {ties.map((tie, i) => (
            <div key={i} className={`p-3 rounded-sm border ${
              tie.affects_qualification ? "border-nk-bad/40 bg-nk-bad/5" : "border-nk-line"
            }`}>
              <p className="text-xs text-nk-mute mb-2">
                #{tie.rank_a} vs #{tie.rank_b} · SS={tie.season_score} · KR={tie.knowledge_rating}
                {tie.affects_qualification && (
                  <span className="ml-2 text-nk-bad font-mono uppercase text-[10px]">CUTS OFF QUALIFICATION</span>
                )}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => onResolveTie(country, tie.user_a, tie.user_b)}
                  data-testid={`ops-resolve-tie-a-${i}`}
                  className="text-xs px-2 py-1 border border-nk-accent text-nk-accent hover:bg-nk-accent/10 rounded-sm"
                >
                  {tie.user_a.username} advances
                </button>
                <span className="text-nk-dim text-xs">vs</span>
                <button
                  onClick={() => onResolveTie(country, tie.user_b, tie.user_a)}
                  data-testid={`ops-resolve-tie-b-${i}`}
                  className="text-xs px-2 py-1 border border-nk-accent text-nk-accent hover:bg-nk-accent/10 rounded-sm"
                >
                  {tie.user_b.username} advances
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DryRunResult({ data }) {
  const champion = data.champion;
  const cc = data.country_cup_projection;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-3 rounded-sm border border-nk-gold/40 bg-nk-gold/[0.04]">
          <p className="text-[10px] font-mono uppercase text-nk-gold mb-1">Projected champion</p>
          {champion ? (
            <div>
              <p className="font-display text-lg">{champion.username}</p>
              <p className="text-xs text-nk-mute font-mono">
                {champion.country} · {champion.total_score} pts
              </p>
            </div>
          ) : <p className="text-sm text-nk-dim">—</p>}
        </div>
        <div className="p-3 rounded-sm border border-nk-line">
          <p className="text-[10px] font-mono uppercase text-nk-mute mb-1">Country Cup (R1+R2)</p>
          <p className="font-mono text-sm">NG · {cc.NG}</p>
          <p className="font-mono text-sm">GH · {cc.GH}</p>
          {cc.winner && <p className="text-xs text-nk-gold mt-1 font-mono">Winner: {cc.winner}</p>}
        </div>
        <div className="p-3 rounded-sm border border-nk-line">
          <p className="text-[10px] font-mono uppercase text-nk-mute mb-1">Seed</p>
          <p className="font-mono text-sm">{data.seed ?? "random"}</p>
        </div>
      </div>

      <div>
        <p className="text-[10px] font-mono uppercase text-nk-mute mb-2">Round-by-round contenders</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-nk-dim border-b border-nk-line">
                <th className="text-left py-2 px-2">Contender</th>
                <th className="text-center px-2">Cty</th>
                <th className="text-center px-2">R1</th>
                <th className="text-center px-2">R2</th>
                <th className="text-center px-2">R3</th>
                <th className="text-center px-2">R4</th>
                <th className="text-right px-2">Total</th>
                <th className="text-right px-2">Out</th>
              </tr>
            </thead>
            <tbody>
              {[...data.contenders].sort((a, b) => b.total_score - a.total_score).map((c) => (
                <tr key={c.user_id} className={`border-b border-nk-line/40 ${c.eliminated ? "text-nk-dim" : "text-nk-text"}`}>
                  <td className="py-1.5 px-2">{c.username}</td>
                  <td className="text-center px-2">{c.country}</td>
                  {[0, 1, 2, 3].map((r) => (
                    <td key={r} className="text-center px-2">{c.round_scores[r] ?? "—"}</td>
                  ))}
                  <td className="text-right px-2 text-nk-accent">{c.total_score}</td>
                  <td className="text-right px-2 text-nk-bad">{c.eliminated_round ? `R${c.eliminated_round}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* -------------------- LIVE CONSOLE TAB -------------------- */
function LiveConsole({ champ, onChanged }) {
  const [broadcast, setBroadcast] = useState("");
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [live, setLive] = useState(null);   // {question, answers, alive_count}

  const running = champ && champ.state === "RUNNING";
  const paused = !!champ?.paused;

  // Poll the currently-live question so admin sees exactly what players see
  useEffect(() => {
    if (!running) { setLive(null); return; }
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/championship/admin/live-question");
        if (alive) setLive(data);
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(id); };
  }, [running, champ?.champ_id]);

  const post = async (path, payload, label) => {
    try {
      await api.post(`/championship${path}`, payload || {});
      toast.success(label);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!champ) {
    return (
      <div className="nk-card p-6 text-center">
        <p className="text-sm text-nk-dim">No championship active. Launch one from the Pre-Event tab.</p>
      </div>
    );
  }

  if (champ.state === "ENDED") {
    return (
      <div className="nk-card p-6 text-center">
        <Trophy size={20} className="mx-auto text-nk-gold mb-2" />
        <p className="font-display text-lg">Championship ended</p>
        <p className="text-xs text-nk-mute mt-1">Check the Post-Event tab.</p>
      </div>
    );
  }

  const alive = champ.contenders.filter(c => !c.eliminated);
  const eliminated = champ.contenders.filter(c => c.eliminated);
  const forceElim = new Set(champ.force_eliminate_uids || []);
  const forceAdv = new Set(champ.force_advance_uids || []);

  return (
    <div className="space-y-6">
      {/* Live question — mirrors exactly what players see */}
      <LiveQuestionMirror live={live} />

      {/* Playback controls */}
      <section className="nk-card p-5">
        <p className="eyebrow mb-3">Playback</p>
        <div className="flex flex-wrap gap-2">
          {paused ? (
            <Btn onClick={() => post("/admin/resume", null, "Resumed")}
                 Icon={Play} label="Resume" tone="accent" testid="ops-resume" />
          ) : (
            <Btn onClick={() => post("/admin/pause", null, "Paused")}
                 Icon={Pause} label="Pause" tone="bad" testid="ops-pause" />
          )}
          <Btn onClick={() => post("/admin/skip-question", null, "Skipped question")}
               Icon={ArrowFatRight} label="Skip question" tone="ghost" testid="ops-skip-q" />
          <Btn onClick={() => post("/admin/skip-round", null, "Skipped round")}
               Icon={ArrowFatRight} label="Skip round" tone="ghost" testid="ops-skip-round" />
          <Btn onClick={() => post("/admin/extend-timer", { seconds: 10 }, "+10s added")}
               Icon={ClockClockwise} label="+10s" tone="ghost" testid="ops-extend-10" />
          <Btn onClick={() => post("/admin/extend-timer", { seconds: 30 }, "+30s added")}
               Icon={ClockClockwise} label="+30s" tone="ghost" testid="ops-extend-30" />
          <Btn onClick={() => {
                 if (!window.confirm("Reset current championship? All state is wiped.")) return;
                 post("/admin/reset", null, "Reset").then(onChanged);
               }}
               Icon={X} label="Reset" tone="bad" testid="ops-reset" />
        </div>
      </section>

      {/* Broadcast */}
      <section className="nk-card p-5">
        <p className="eyebrow mb-3 flex items-center gap-2"><Megaphone size={12} /> Broadcast to viewers</p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={broadcast}
            onChange={(e) => setBroadcast(e.target.value.slice(0, 280))}
            placeholder="Type an announcement… (280 chars)"
            data-testid="ops-broadcast-input"
            className="flex-1 px-3 py-2 text-sm bg-nk-charcoal border border-nk-line rounded-sm focus:border-nk-gold outline-none"
          />
          <Btn
            onClick={async () => {
              if (!broadcast.trim()) return;
              await post("/admin/broadcast-message", { text: broadcast.trim() }, "Broadcast sent");
              setBroadcast("");
            }}
            Icon={Broadcast} label="Send" tone="accent" testid="ops-broadcast-send" />
        </div>
        {(champ.admin_messages || []).slice(-3).reverse().map((m, i) => (
          <div key={i} className="mt-2 text-xs text-nk-mute font-mono">
            <span className="text-nk-gold">{m.by}</span> · {m.text}
          </div>
        ))}
      </section>

      {/* Contenders */}
      <section>
        <p className="eyebrow mb-3">Alive · {alive.length}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {alive.map((c) => (
            <ContenderRow
              key={c.user_id}
              c={c}
              queuedElim={forceElim.has(c.user_id)}
              queuedAdv={forceAdv.has(c.user_id)}
              onElim={() => post("/admin/force-eliminate", { user_id: c.user_id }, `Queued ${c.username} for elim`)}
              onAdv={() => post("/admin/force-advance", { user_id: c.user_id }, `${c.username} force-advanced`)}
            />
          ))}
        </div>
      </section>

      {eliminated.length > 0 && (
        <section>
          <p className="eyebrow mb-3">Eliminated · {eliminated.length}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 opacity-60">
            {eliminated.map((c) => (
              <div key={c.user_id} className="flex items-center gap-3 px-3 py-2 border border-nk-line rounded-sm">
                <AvatarIcon id={c.avatar} size={20} />
                <span className="text-sm flex-1 truncate line-through">{c.username}</span>
                <span className="text-[10px] font-mono text-nk-bad">R{c.eliminated_round}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Override question */}
      <section className="nk-card p-5">
        <button
          onClick={() => setOverrideOpen(v => !v)}
          data-testid="ops-toggle-override"
          className="flex items-center justify-between w-full">
          <p className="eyebrow">Question override (advanced)</p>
          <span className="text-xs text-nk-mute">{overrideOpen ? "hide" : "expand"}</span>
        </button>
        {overrideOpen && <QuestionOverride champ={champ} onChanged={onChanged} />}
      </section>
    </div>
  );
}

function ContenderRow({ c, queuedElim, queuedAdv, onElim, onAdv }) {
  return (
    <div className={`flex items-center gap-3 px-3 py-2 border rounded-sm ${
      queuedElim ? "border-nk-bad/60 bg-nk-bad/5"
      : queuedAdv ? "border-nk-gold/60 bg-nk-gold/5"
      : "border-nk-line"
    }`}>
      <AvatarIcon id={c.avatar} size={22} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm truncate">{c.username}</span>
          <CountryFlag country={c.country} size={10} />
        </div>
        <div className="flex items-center gap-2 text-[10px] text-nk-dim font-mono">
          <span>Seed #{c.seed}</span>
          <span>·</span>
          <span>Total {c.total_score}</span>
          {queuedElim && <span className="text-nk-bad">· QUEUED ELIM</span>}
          {queuedAdv && <span className="text-nk-gold">· FORCE ADVANCE</span>}
        </div>
      </div>
      {/* AI Lifelines indicator (replaces old lives/shield) */}
      <div className="flex items-center gap-1 text-[10px] font-mono mr-2"
           title={`${(c.lifelines_total ?? 0) - (c.lifelines_used?.length ?? 0)} of ${c.lifelines_total ?? 0} lifelines left`}>
        <span className={((c.lifelines_total ?? 0) - (c.lifelines_used?.length ?? 0)) > 0 ? "text-nk-gold" : "text-nk-line"}>
          ✦ {(c.lifelines_total ?? 0) - (c.lifelines_used?.length ?? 0)}/{c.lifelines_total ?? 0}
        </span>
      </div>
      <button
        onClick={onAdv}
        data-testid={`ops-adv-${c.user_id}`}
        title="Force advance"
        className="p-1.5 border border-nk-line hover:border-nk-gold hover:text-nk-gold rounded-sm">
        <ShieldCheck size={13} />
      </button>
      <button
        onClick={onElim}
        data-testid={`ops-elim-${c.user_id}`}
        title="Force eliminate"
        className="p-1.5 border border-nk-line hover:border-nk-bad hover:text-nk-bad rounded-sm">
        <X size={13} />
      </button>
    </div>
  );
}

function QuestionOverride({ champ, onChanged }) {
  const [round, setRound] = useState((champ.current_round || 1));
  const [qIdx, setQIdx] = useState(0);
  const [qid, setQid] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!qid.trim()) return toast.error("Enter a qid");
    setBusy(true);
    try {
      await api.post("/championship/admin/override-question", {
        round: parseInt(round, 10),
        question_index: parseInt(qIdx, 10),
        qid: qid.trim(),
      });
      toast.success("Question overridden");
      setQid("");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
      <div>
        <label className="text-[10px] uppercase font-mono text-nk-dim block mb-1">Round</label>
        <input type="number" min="1" max="4" value={round} onChange={(e) => setRound(e.target.value)}
               data-testid="ops-override-round"
               className="w-full px-3 py-2 text-sm bg-nk-charcoal border border-nk-line rounded-sm font-mono" />
      </div>
      <div>
        <label className="text-[10px] uppercase font-mono text-nk-dim block mb-1">Q index</label>
        <input type="number" min="0" value={qIdx} onChange={(e) => setQIdx(e.target.value)}
               data-testid="ops-override-qidx"
               className="w-full px-3 py-2 text-sm bg-nk-charcoal border border-nk-line rounded-sm font-mono" />
      </div>
      <div className="md:col-span-2">
        <label className="text-[10px] uppercase font-mono text-nk-dim block mb-1">Replacement qid</label>
        <input type="text" value={qid} onChange={(e) => setQid(e.target.value)} placeholder="q_xxxxxxx"
               data-testid="ops-override-qid"
               className="w-full px-3 py-2 text-sm bg-nk-charcoal border border-nk-line rounded-sm font-mono" />
      </div>
      <div className="md:col-span-4">
        <Btn onClick={submit} Icon={ArrowFatRight}
             label={busy ? "Submitting…" : "Override this question"}
             tone="gold" testid="ops-override-submit" />
        <p className="text-[10px] text-nk-dim mt-2 font-mono">
          Only future questions can be overridden. Current or past questions are locked.
        </p>
      </div>
    </div>
  );
}

/* -------------------- POST-EVENT TAB -------------------- */
function PostEvent({ champ, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/championship/admin/post-event");
      setData(data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
    try {
      const { data } = await api.get("/season/current");
      setSeason(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [champ?.state]);

  const pauseScoring = async () => {
    try {
      await api.post("/season/admin/pause-scoring");
      toast.success("Season scoring paused");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const resumeScoring = async () => {
    try {
      await api.post("/season/admin/resume-scoring");
      toast.success("Season scoring resumed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const startNextSeason = async () => {
    if (!window.confirm(`Start Season ${(season?.season_number || 1) + 1}? This resets SS + broadcasts a welcome banner.`)) return;
    try {
      await api.post("/season/admin/start-next-season");
      toast.success(`Season ${(season?.season_number || 1) + 1} started`);
      onChanged();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const paused = season?.paused_scoring;

  if (loading) return <p className="text-sm text-nk-dim py-8 text-center">Loading…</p>;
  const noEndedChamp = !data || data.ok === false;

  return (
    <div className="space-y-6">
      {noEndedChamp && (
        <div className="nk-card p-6 text-center">
          <p className="text-sm text-nk-dim">
            No ended championship yet.
            {data?.state && <span className="ml-1 font-mono">Current state: {data.state}</span>}
          </p>
        </div>
      )}
      {/* Champion card */}
      {!noEndedChamp && data.champion && (
        <section className="nk-card p-6 border-nk-gold/50 bg-gradient-to-br from-nk-gold/[0.06] to-transparent">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-20 h-20 rounded-sm border border-nk-gold flex items-center justify-center bg-nk-charcoal">
              <AvatarIcon id={data.champion.avatar} size={54} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono uppercase text-nk-gold mb-1 flex items-center gap-1.5">
                <Crown size={11} weight="fill" /> Champion · Season {data.season_number}
              </p>
              <p className="font-display text-3xl">{data.champion.username}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-nk-mute font-mono">
                <span className="flex items-center gap-1">
                  <CountryFlag country={data.champion.country} size={10} /> {data.champion.country}
                </span>
                <span>Seed #{data.champion.seed}</span>
                <span>{data.champion.total_score} pts</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Country Cup */}
      {!noEndedChamp && data.country_cup?.winner && (
        <section className="nk-card p-5">
          <p className="eyebrow mb-3">Country Cup</p>
          <div className="flex items-center gap-3">
            <CountryFlag country={data.country_cup.winner} size={20} />
            <div>
              <p className="font-display text-lg">
                {data.country_cup.winner === "NG" ? "Nigeria" : "Ghana"} wins the Country Cup
              </p>
              <p className="text-xs text-nk-mute font-mono">
                NG · {data.country_cup.NG?.score ?? 0} pts &nbsp;·&nbsp;
                GH · {data.country_cup.GH?.score ?? 0} pts
              </p>
              {data.country_cup.granted && (
                <p className="text-xs text-nk-accent font-mono mt-1">
                  NK+ granted → qualifiers: {data.country_cup.granted.qualified} · citizens: {data.country_cup.granted.citizens}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Full breakdown */}
      {!noEndedChamp && (
      <section>
        <p className="eyebrow mb-3">Final standings</p>
        <div className="nk-card p-4 overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-nk-dim border-b border-nk-line">
                <th className="text-left py-2 px-2">#</th>
                <th className="text-left px-2">Contender</th>
                <th className="text-center px-2">Cty</th>
                {(data.rounds || []).map((_, i) => (
                  <th key={i} className="text-center px-2">R{i + 1}</th>
                ))}
                <th className="text-right px-2">Total</th>
                <th className="text-right px-2">Out</th>
              </tr>
            </thead>
            <tbody>
              {[...data.contenders].sort((a, b) => b.total_score - a.total_score).map((c, i) => (
                <tr key={c.user_id} className={`border-b border-nk-line/40 ${c.eliminated ? "text-nk-dim" : "text-nk-text"}`}>
                  <td className="py-1.5 px-2 text-nk-gold">{i + 1}</td>
                  <td className="px-2">{c.username}</td>
                  <td className="text-center px-2">{c.country}</td>
                  {(data.rounds || []).map((_, ri) => (
                    <td key={ri} className="text-center px-2">{c.round_scores?.[ri] ?? "—"}</td>
                  ))}
                  <td className="text-right px-2 text-nk-accent">{c.total_score}</td>
                  <td className="text-right px-2 text-nk-bad">{c.eliminated_round ? `R${c.eliminated_round}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {/* Season lifecycle controls */}
      <section className="nk-card p-5 border-nk-accent/40 bg-nk-accent/[0.03]">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-display text-lg">Season {season?.season_number || 1} lifecycle</p>
            <p className="text-xs text-nk-mute mt-1 max-w-md">
              {paused
                ? "SS crediting is PAUSED. Players can still play (ratings tick) but season score is frozen."
                : "Manually pause SS crediting during Day 31 (or if Day 31 spans multiple days). Ratings still update; championship SS still counts."}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {paused ? (
              <Btn onClick={resumeScoring} Icon={Play} label="Resume scoring"
                   tone="accent" testid="ops-season-resume-scoring" />
            ) : (
              <Btn onClick={pauseScoring} Icon={Pause} label="Pause scoring"
                   tone="bad" testid="ops-season-pause-scoring" />
            )}
            <Btn onClick={startNextSeason} Icon={Play}
                 label={`Start Season ${(season?.season_number || 1) + 1}`}
                 tone="gold" testid="ops-start-next-season" />
          </div>
        </div>
      </section>
    </div>
  );
}

/* -------------------- INVITES TAB -------------------- */
function InvitesTab({ champ }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/championship/admin/invites");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invites");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [champ?.champ_id]);

  const sendAll = async () => {
    if (!window.confirm("Send/refresh invites to all current Top-5 qualifiers for both countries?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/championship/admin/invites/send");
      toast.success(`Sent — ${data.created} new · ${data.updated} refreshed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSending(false); }
  };

  if (!champ) {
    return (
      <div className="nk-card p-6 text-center">
        <p className="text-sm text-nk-mute">No championship yet. Start one from Pre-Event first.</p>
      </div>
    );
  }

  const invites = data?.invites || [];
  const counts = data?.counts || {};

  return (
    <div className="space-y-6">
      <section className="nk-card p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="eyebrow">Invitation status</p>
          <p className="text-xs text-nk-mute mt-1">
            Pending: <b className="text-nk-warn">{counts.pending || 0}</b> · Accepted:{" "}
            <b className="text-nk-gold">{counts.accepted || 0}</b> · Declined:{" "}
            <b className="text-nk-bad">{counts.declined || 0}</b>
          </p>
        </div>
        <Btn onClick={sendAll} Icon={EnvelopeSimple}
             label={sending ? "Sending…" : "Send / refresh invites"}
             tone="accent" testid="ops-send-invites" />
      </section>

      <section>
        <p className="eyebrow mb-3">All invites</p>
        {loading ? (
          <p className="text-sm text-nk-mute py-4 text-center">Loading…</p>
        ) : invites.length === 0 ? (
          <div className="nk-card p-6 text-center">
            <p className="text-sm text-nk-mute">No invites yet. Click <b>Send / refresh invites</b> to notify current Top-5.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {invites.map((inv) => (
              <div key={inv.user_id} className="flex items-center gap-3 px-3 py-2 border border-nk-line rounded-sm">
                <span className="text-nk-gold font-mono text-xs w-6">#{inv.seed}</span>
                <CountryFlag country={inv.country} size={12} />
                <span className="text-sm flex-1 truncate">{inv.username}</span>
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm border ${
                  inv.status === "accepted" ? "border-nk-gold/40 bg-nk-gold/10 text-nk-gold"
                  : inv.status === "declined" ? "border-nk-bad/40 bg-nk-bad/10 text-nk-bad"
                  : "border-nk-warn/40 bg-nk-warn/10 text-nk-warn"
                }`}>
                  {inv.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* -------------------- QUESTIONS TAB -------------------- */
const THEME_ROWS = [
  { key: "african_history_politics", label: "R1 · African History & Politics" },
  { key: "world_civ_geography", label: "R2 · World Civ & Geography" },
  { key: "arts_music_literature", label: "R3 · Arts, Music & Literature" },
  { key: "grand_final_mixed", label: "R4 · Grand Final (Mixed)" },
];

function QuestionsTab({ champ }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testPlay, setTestPlay] = useState(null);
  const [bank, setBank] = useState(null);          // {questions, counts_by_theme, total}
  const [showBank, setShowBank] = useState(false);
  const [bankFilter, setBankFilter] = useState({ theme_key: "", difficulty: "medium_hard" });
  const [generating, setGenerating] = useState(null);   // theme_key currently running

  const load = async () => {
    if (!champ) return;
    setLoading(true);
    try {
      const { data } = await api.get("/championship/admin/rounds/preview");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  const loadBank = async () => {
    try {
      const params = new URLSearchParams();
      if (bankFilter.theme_key) params.set("theme_key", bankFilter.theme_key);
      if (bankFilter.difficulty) params.set("difficulty", bankFilter.difficulty);
      const { data } = await api.get(`/championship/admin/bank?${params.toString()}`);
      setBank(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load bank"); }
  };
  useEffect(() => { load(); }, [champ?.champ_id]);
  useEffect(() => { if (showBank) loadBank(); }, [showBank, bankFilter.theme_key, bankFilter.difficulty]);

  const flagQuestion = async (qid) => {
    if (!window.confirm(`Flag ${qid} as broken/ambiguous?`)) return;
    try {
      await api.post("/championship/admin/questions/flag", { qid });
      toast.success("Flagged");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const generateForTheme = async (theme_key, count = 10) => {
    setGenerating(theme_key);
    try {
      const { data } = await api.post("/admin/questions/ai/championship-generate", {
        count, theme_key, difficulty: "medium_hard",
      });
      toast.success(`Generated ${data.inserted} of ${data.received}`);
      if (showBank) loadBank();
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.data?.message || "Generation failed";
      toast.error(typeof msg === "string" ? msg : "Generation failed");
    } finally { setGenerating(null); }
  };

  return (
    <div className="space-y-6">
      {/* Generation panel — always visible */}
      <section className="nk-card p-5">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <p className="eyebrow">Generate championship questions · AI</p>
            <p className="text-xs text-nk-mute mt-1 max-w-xl">
              Uses the Emergent LLM to write medium/hard questions for each round&apos;s theme.
              Generated questions are tagged <span className="font-mono text-nk-gold">track=championship</span>{" "}
              and stored in the bank. Curate before Day 31.
            </p>
          </div>
          <button onClick={() => setShowBank(v => !v)}
                  data-testid="ops-toggle-bank"
                  className="text-xs px-3 py-1.5 border border-nk-line hover:border-nk-gold rounded-sm">
            {showBank ? "Hide bank browser" : "Browse championship bank"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {THEME_ROWS.map((t) => (
            <div key={t.key} className="flex items-center gap-3 px-3 py-2 border border-nk-line rounded-sm">
              <span className="flex-1 text-sm">{t.label}</span>
              {bank?.counts_by_theme?.[t.key] != null && (
                <span className="text-[10px] font-mono text-nk-mute">
                  {bank.counts_by_theme[t.key]} in bank
                </span>
              )}
              <button
                onClick={() => generateForTheme(t.key, 10)}
                disabled={generating === t.key}
                data-testid={`ops-generate-${t.key}`}
                className="text-xs px-3 py-1.5 border border-nk-gold text-nk-gold hover:bg-nk-gold/10 rounded-sm disabled:opacity-50">
                {generating === t.key ? "Generating…" : "Generate 10"}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Bank browser (optional expand) */}
      {showBank && (
        <BankBrowser bank={bank} filter={bankFilter} onFilter={setBankFilter} onReload={loadBank} />
      )}

      {/* Locked-in per-round questions */}
      {!champ && (
        <div className="nk-card p-6 text-center">
          <p className="text-sm text-nk-mute">
            No championship scheduled — the round set below appears after you launch one.
          </p>
        </div>
      )}
      {loading && <p className="text-sm text-nk-mute py-4 text-center">Loading question set…</p>}
      {data && (
        <>
          <div className="nk-card p-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="eyebrow">Locked-in question set · {data.rounds.length * 5} questions</p>
              <p className="text-xs text-nk-mute mt-1">
                These are what players will see on Day 31. Test-play a round to verify.
              </p>
            </div>
            <Btn onClick={load} Icon={ArrowCounterClockwise} label="Reload" tone="ghost" testid="ops-questions-reload" />
          </div>
          {data.rounds.map((r) => (
            <section key={r.round} className="nk-card p-5">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                  <p className="text-[10px] font-mono uppercase text-nk-gold mb-1">
                    Round {r.round}
                    {r.country_cup_round && <span className="ml-2 text-nk-gold">· COUNTRY CUP</span>}
                    {r.african_only && <span className="ml-2 text-nk-accent">· AFRICA ONLY</span>}
                  </p>
                  <p className="font-display text-xl">{r.theme}</p>
                </div>
                <Btn
                  onClick={() => setTestPlay({ round: r, index: 0, picked: null, revealed: false })}
                  Icon={Play} label="Test-play this round" tone="gold"
                  testid={`ops-testplay-r${r.round}`}
                />
              </div>
              <div className="space-y-3">
                {r.questions.map((q) => (
                  <QuestionPreviewRow key={q.qid} q={q} onFlag={() => flagQuestion(q.qid)} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {testPlay && <TestPlayModal state={testPlay} onChange={setTestPlay} />}
    </div>
  );
}

function BankBrowser({ bank, filter, onFilter, onReload }) {
  const [expandedQid, setExpandedQid] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const toggle = (qid) => {
    const next = new Set(selected);
    if (next.has(qid)) next.delete(qid); else next.add(qid);
    setSelected(next);
  };
  const toggleAll = () => {
    if (!bank?.questions) return;
    if (selected.size === bank.questions.length) setSelected(new Set());
    else setSelected(new Set(bank.questions.map(q => q.qid)));
  };
  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} championship question${selected.size === 1 ? "" : "s"} permanently?`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/championship/admin/bank/bulk-delete", { qids: [...selected] });
      toast.success(`Deleted ${data.deleted}`);
      setSelected(new Set());
      onReload && onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  return (
    <section className="nk-card p-5">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <p className="eyebrow flex-1">Championship bank</p>
        <select
          value={filter.theme_key}
          onChange={(e) => onFilter({ ...filter, theme_key: e.target.value })}
          data-testid="bank-filter-theme"
          className="text-xs bg-nk-charcoal border border-nk-line rounded-sm px-2 py-1.5">
          <option value="">All themes</option>
          {THEME_ROWS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <select
          value={filter.difficulty}
          onChange={(e) => onFilter({ ...filter, difficulty: e.target.value })}
          data-testid="bank-filter-difficulty"
          className="text-xs bg-nk-charcoal border border-nk-line rounded-sm px-2 py-1.5">
          <option value="medium_hard">Medium + Hard</option>
          <option value="hard">Hard only</option>
          <option value="medium">Medium only</option>
          <option value="">Any difficulty</option>
        </select>
        <button onClick={onReload} className="text-xs px-3 py-1.5 border border-nk-line hover:border-nk-gold rounded-sm">
          Refresh
        </button>
      </div>

      {/* Selection toolbar */}
      {bank?.questions?.length > 0 && (
        <div className="flex items-center gap-3 mb-3 flex-wrap p-2 border border-nk-line rounded-sm bg-nk-charcoal/30">
          <label className="flex items-center gap-2 text-xs text-nk-mute cursor-pointer">
            <input type="checkbox"
                   checked={selected.size === bank.questions.length && bank.questions.length > 0}
                   onChange={toggleAll}
                   data-testid="bank-select-all"
                   className="accent-nk-gold" />
            Select all ({bank.questions.length})
          </label>
          <span className="text-xs text-nk-dim">·</span>
          <span className="text-xs text-nk-gold font-mono">{selected.size} selected</span>
          <div className="flex-1" />
          <button
            onClick={deleteSelected}
            disabled={selected.size === 0 || deleting}
            data-testid="bank-delete-selected"
            className="text-xs px-3 py-1.5 border border-nk-bad text-nk-bad hover:bg-nk-bad/10 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed">
            {deleting ? "Deleting…" : `Delete ${selected.size || ""}`}
          </button>
        </div>
      )}

      {!bank ? <p className="text-sm text-nk-mute py-6 text-center">Loading…</p> :
       bank.questions.length === 0 ? (
        <p className="text-sm text-nk-mute py-6 text-center">
          No questions match. Generate some above.
        </p>
       ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
          {bank.questions.map((q) => (
            <div key={q.qid} className={`p-3 border rounded-sm transition-colors ${
              selected.has(q.qid) ? "border-nk-gold bg-nk-gold/5" : "border-nk-line"
            }`}>
              <div className="flex items-start gap-2">
                <input type="checkbox"
                       checked={selected.has(q.qid)}
                       onChange={() => toggle(q.qid)}
                       data-testid={`bank-select-${q.qid}`}
                       className="mt-1 accent-nk-gold" />
                <button
                  onClick={() => setExpandedQid(expandedQid === q.qid ? null : q.qid)}
                  className="text-sm text-left flex-1 hover:text-nk-gold transition-colors">
                  {q.body || "(no body)"}
                </button>
                <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 border rounded-sm ${
                  q.difficulty === "hard" ? "border-nk-bad text-nk-bad"
                  : q.difficulty === "medium" ? "border-nk-warn text-nk-warn"
                  : "border-nk-line text-nk-mute"
                }`}>
                  {q.difficulty || "?"}
                </span>
              </div>
              {expandedQid === q.qid && (
                <ul className="mt-2 space-y-1 pl-7">
                  {(q.options || []).map((o, i) => (
                    <li key={i} className={`text-xs pl-3 py-1 ${
                      i === q.answer_index ? "text-nk-gold border-l-2 border-nk-gold pl-2" : "text-nk-mute"
                    }`}>
                      <span className="font-mono mr-2">{String.fromCharCode(65 + i)}.</span>{o}
                      {i === q.answer_index && <span className="ml-2 text-[10px]">✓</span>}
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[10px] font-mono text-nk-dim mt-1 pl-7">
                {q.qid} · {q.theme_key || "unthemed"} · {q.category || "no category"}
              </p>
            </div>
          ))}
        </div>
       )}
    </section>
  );
}

function QuestionPreviewRow({ q, onFlag }) {
  const [expanded, setExpanded] = useState(false);
  if (q.missing) {
    return (
      <div className="p-3 border border-nk-bad rounded-sm bg-nk-bad/5">
        <p className="text-sm text-nk-bad">
          <b>Q{q.index + 1}</b> · {q.qid} · <b>MISSING FROM BANK</b> — regenerate question set
        </p>
      </div>
    );
  }
  return (
    <div className={`p-3 border rounded-sm ${q.flagged ? "border-nk-bad/50 bg-nk-bad/5" : "border-nk-line"}`}>
      <div className="flex items-start gap-2">
        <span className="text-[10px] font-mono text-nk-dim w-8 pt-0.5">Q{q.index + 1}</span>
        <div className="flex-1 min-w-0">
          <button onClick={() => setExpanded(v => !v)}
                  className="text-sm text-left w-full hover:text-nk-gold transition-colors">
            {q.body || "(no body)"}
          </button>
          {expanded && (
            <ul className="mt-2 space-y-1">
              {(q.options || []).map((o, i) => (
                <li key={i}
                    className={`text-xs pl-3 py-1 rounded-sm ${
                      i === q.answer_index
                        ? "bg-nk-gold/10 text-nk-gold border-l-2 border-nk-gold"
                        : "text-nk-mute"
                    }`}>
                  <span className="font-mono mr-2">{String.fromCharCode(65 + i)}.</span>
                  {o}
                  {i === q.answer_index && <span className="ml-2 text-[10px]">✓ CORRECT</span>}
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] font-mono text-nk-dim mt-1">
            {q.qid} · {q.category || "no category"}
            {q.flagged && <span className="text-nk-bad ml-2">· FLAGGED</span>}
          </p>
        </div>
        {!q.flagged && (
          <button onClick={onFlag}
                  data-testid={`ops-flag-${q.qid}`}
                  title="Flag as broken/ambiguous"
                  className="p-1.5 border border-nk-line rounded-sm hover:border-nk-bad hover:text-nk-bad">
            <Flag size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * TestPlayModal — solo rehearsal of a single round.
 * NO writes: pure client-side timer + reveal, so admins can verify pacing
 * and question quality before Day 31 goes live.
 */
function TestPlayModal({ state, onChange }) {
  const { round, index, picked, revealed } = state;
  const q = round.questions[index];
  const [timer, setTimer] = useState(20);

  useEffect(() => {
    if (revealed) return;
    if (timer <= 0) { onChange({ ...state, revealed: true }); return; }
    const t = setTimeout(() => setTimer(timer - 1), 1000);
    return () => clearTimeout(t);
  }, [timer, revealed]);

  useEffect(() => { setTimer(20); }, [index]);

  const pick = (i) => onChange({ ...state, picked: i, revealed: true });
  const next = () => {
    if (index + 1 >= round.questions.length) { onChange(null); return; }
    onChange({ round, index: index + 1, picked: null, revealed: false });
  };
  const close = () => onChange(null);

  return (
    <div className="fixed inset-0 z-50 bg-nk-bg/95 backdrop-blur flex items-center justify-center p-4"
         data-testid="testplay-modal">
      <div className="max-w-2xl w-full nk-card p-6 border-nk-gold/40">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] font-mono uppercase text-nk-gold">Test-play · Round {round.round} · {round.theme}</p>
            <p className="text-xs text-nk-mute font-mono">Q{index + 1} of {round.questions.length}</p>
          </div>
          <button onClick={close} data-testid="testplay-close"
                  className="p-1.5 border border-nk-line rounded-sm hover:border-nk-bad">
            <X size={14} />
          </button>
        </div>

        <p className="text-lg font-medium mb-4">{q.body || "(no body)"}</p>
        <ul className="space-y-2 mb-4">
          {(q.options || []).map((o, i) => {
            const isCorrect = i === q.answer_index;
            const isMyPick = picked === i;
            const cls = revealed
              ? isCorrect ? "border-nk-gold bg-nk-gold/10 text-nk-gold"
              : isMyPick ? "border-nk-bad bg-nk-bad/10 text-nk-bad"
              : "border-nk-line opacity-60"
              : "border-nk-line hover:border-nk-gold hover:bg-nk-gold/5";
            return (
              <li key={i}>
                <button
                  disabled={revealed}
                  onClick={() => pick(i)}
                  data-testid={`testplay-opt-${i}`}
                  className={`w-full text-left border rounded-sm px-4 py-2 text-sm transition-colors disabled:cursor-not-allowed ${cls}`}
                >
                  <span className="text-nk-mute mr-2">{String.fromCharCode(65 + i)}.</span> {o}
                  {revealed && isCorrect && <span className="ml-2 text-[10px]">✓ CORRECT</span>}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="flex items-center justify-between gap-3">
          {!revealed ? (
            <p className="font-mono text-2xl text-nk-gold">{timer}s</p>
          ) : (
            <p className="text-xs font-mono text-nk-mute">
              {picked === q.answer_index ? "✓ Correct" : picked == null ? "⌛ Timed out" : "✕ Wrong"}
            </p>
          )}
          {revealed && (
            <Btn onClick={next} Icon={ArrowFatRight}
                 label={index + 1 >= round.questions.length ? "Finish" : "Next question"}
                 tone="gold" testid="testplay-next" />
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * LiveQuestionMirror — shows the admin EXACTLY what players are currently
 * seeing, plus live tally of who picked what. This is how admins catch
 * a broken question mid-broadcast.
 */
function LiveQuestionMirror({ live }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  if (!live || !live.question) {
    return (
      <section className="nk-card p-5 border-dashed" data-testid="live-question-mirror">
        <p className="eyebrow mb-2">Live question mirror</p>
        <p className="text-xs text-nk-mute">
          {live?.reason ? `Waiting… (${live.reason})` : "No live question right now."}
        </p>
      </section>
    );
  }

  const q = live.question;
  const startedMs = new Date(q.started_at).getTime();
  const elapsed = Math.max(0, Math.floor((now - startedMs) / 1000));
  const remaining = Math.max(0, q.duration - elapsed);
  const tally = q.options.map((_, i) => live.answers.filter(a => a.option_index === i).length);
  const totalAnswered = live.answers.length;
  const alive = live.alive_count || 1;

  return (
    <section className="nk-card p-5 border-nk-gold/40" data-testid="live-question-mirror">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <p className="text-[10px] font-mono uppercase text-nk-gold mb-1">
            LIVE · Round {q.round} · Q{q.index + 1} · {q.difficulty || "?"} · {q.category || "no category"}
          </p>
          <p className="text-lg font-medium">{q.body}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-mono text-nk-dim uppercase">Time left</p>
          <p className={`font-mono text-3xl ${remaining < 5 ? "text-nk-bad animate-pulse" : "text-nk-gold"}`}>
            {remaining}s
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        {q.options.map((o, i) => {
          const isCorrect = i === q.answer_index;
          const count = tally[i];
          const pct = totalAnswered ? Math.round((count / totalAnswered) * 100) : 0;
          return (
            <div key={i} className={`px-3 py-2 border rounded-sm relative overflow-hidden ${
              isCorrect ? "border-nk-gold bg-nk-gold/10" : "border-nk-line"
            }`}>
              <div className="absolute inset-y-0 left-0 bg-nk-gold/5"
                   style={{ width: `${pct}%` }} />
              <div className="relative flex items-center gap-2 text-sm">
                <span className="font-mono text-nk-mute w-6">{String.fromCharCode(65 + i)}.</span>
                <span className="flex-1">{o}</span>
                {isCorrect && <span className="text-[10px] font-mono text-nk-gold">✓ CORRECT</span>}
                <span className="font-mono text-xs text-nk-mute">{count} · {pct}%</span>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] font-mono text-nk-dim mt-3">
        {totalAnswered} / {alive} contenders answered ·{" "}
        {live.answers.slice(-5).map(a => a.username || a.user_id).join(", ") || "no submissions yet"}
      </p>
    </section>
  );
}

